import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Camera, CheckCircle2, ImageIcon, ShieldCheck, Sparkles, Upload, X } from "lucide-react";
import { API } from "@/lib/auth";

const MAX_MB = 10;

export default function ColourMatch() {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    current_hair: "",
    desired_look: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleFile = (f) => {
    if (!f) return;
    if (!/^image\/(jpe?g|png|webp|heic|heif)$/i.test(f.type) && !/\.(jpe?g|png|webp|heic|heif)$/i.test(f.name)) {
      toast.error("Please upload a JPG, PNG, WebP or HEIC photo.");
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Photo is too large (max ${MAX_MB} MB).`);
      return;
    }
    setFile(f);
    // Preview only for browser-renderable formats (skip HEIC).
    if (/heic|heif/i.test(f.type) || /\.hei[cf]$/i.test(f.name)) {
      setPreview(null);
    } else {
      const url = URL.createObjectURL(f);
      setPreview(url);
    }
  };

  const onSelect = (e) => handleFile(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files?.[0]);
  };
  const clearPhoto = () => {
    setFile(null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please add a photo of your current hair.");
    if (!form.name.trim() || !form.email.trim()) {
      return toast.error("Please add your name and email.");
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      Object.entries(form).forEach(([k, v]) => {
        if (v?.trim()) fd.append(k, v.trim());
      });
      await axios.post(`${API}/colour-match`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSubmitted(true);
      toast.success("Photo received — Malek will be in touch shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="page-colour-match-success" className="pt-40 pb-24 min-h-[70vh]">
        <div className="mx-auto max-w-[720px] px-6 text-center">
          <div className="w-20 h-20 rounded-full bg-blush mx-auto flex items-center justify-center">
            <CheckCircle2 size={36} className="text-[hsl(var(--burgundy))]" />
          </div>
          <div className="overline mt-8">Photo received</div>
          <h1 className="font-display text-5xl mt-3 tracking-tight text-balance">
            Thank you — we've got it.
          </h1>
          <p className="mt-4 text-[hsl(var(--onyx)/0.65)] leading-relaxed">
            Malek will personally review your photo and reply with a
            recommended shade from the NøoksProRange colour chart —
            usually within one working day. Please check your inbox for a
            confirmation from us.
          </p>
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <Link
              to="/shop"
              className="btn-pill btn-primary"
              data-testid="colour-match-shop-cta"
            >
              Browse extensions <ArrowUpRight size={16} />
            </Link>
            <Link to="/" className="btn-pill btn-outline">Back to home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="page-colour-match" className="pt-32 pb-24">
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
        <div className="grid gap-14 lg:grid-cols-12">
          {/* Left column — story */}
          <div className="lg:col-span-5">
            <div className="overline">NøoksProRange</div>
            <h1 className="font-display text-5xl sm:text-6xl mt-4 tracking-tight text-balance">
              Colour Match <span className="italic font-light">Consultation.</span>
            </h1>
            <p className="mt-6 text-[hsl(var(--onyx)/0.65)] leading-relaxed">
              Not sure which shade to pick? Send us a clear photo of your
              current hair and Malek will personally recommend the closest
              match from the NøoksProRange chart. Free, no obligation,
              usually within one working day.
            </p>

            <ul className="mt-10 space-y-5">
              {[
                { Icon: Camera, title: "1. Take a natural photo", text: "Daylight, no filters, hair down and dry. A back-of-head shot works best." },
                { Icon: Upload, title: "2. Upload & tell us your goal", text: "Attach the photo, share your current & desired look, add any notes." },
                { Icon: Sparkles, title: "3. Get your personalised match", text: "Malek replies with the recommended NøoksProRange code + shopping link." },
              ].map(({ Icon, title, text }) => (
                <li key={title} className="flex gap-4">
                  <div className="w-11 h-11 rounded-full bg-blush shrink-0 flex items-center justify-center">
                    <Icon size={18} className="text-[hsl(var(--burgundy))]" />
                  </div>
                  <div>
                    <div className="font-display text-lg">{title}</div>
                    <div className="text-sm text-[hsl(var(--onyx)/0.6)] leading-relaxed mt-1">{text}</div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex items-center gap-3 text-xs text-[hsl(var(--onyx)/0.55)]">
              <ShieldCheck size={14} className="text-[hsl(var(--gold))]" />
              Your photo is used only for the match and stays confidential.
            </div>
          </div>

          {/* Right column — form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              className="rounded-[2rem] bg-white border border-[hsl(var(--onyx)/0.08)] p-8 sm:p-10 shadow-[0_20px_60px_-30px_rgba(20,10,15,0.15)]"
            >
              {/* Photo uploader */}
              <label className="overline">Your photo *</label>
              <div
                onDrop={onDrop}
                onDragOver={(e) => e.preventDefault()}
                className="mt-3 rounded-3xl border-2 border-dashed border-[hsl(var(--onyx)/0.15)] hover:border-[hsl(var(--burgundy))] transition-colors bg-blush/40 p-6"
              >
                {file ? (
                  <div className="flex items-center gap-4">
                    <div className="w-24 h-24 rounded-2xl overflow-hidden bg-white border border-[hsl(var(--onyx)/0.08)] shrink-0 flex items-center justify-center">
                      {preview ? (
                        <img src={preview} alt="Preview" className="w-full h-full object-cover" />
                      ) : (
                        <ImageIcon size={28} className="text-[hsl(var(--onyx)/0.35)]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{file.name}</div>
                      <div className="text-xs text-[hsl(var(--onyx)/0.55)] mt-1">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={clearPhoto}
                      data-testid="colour-match-clear-photo"
                      className="w-9 h-9 rounded-full hover:bg-[hsl(var(--onyx)/0.06)] flex items-center justify-center"
                      aria-label="Remove photo"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    data-testid="colour-match-pick-photo"
                    className="w-full flex flex-col items-center gap-2 py-6 text-center"
                  >
                    <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center border border-[hsl(var(--onyx)/0.08)]">
                      <Upload size={20} className="text-[hsl(var(--burgundy))]" />
                    </div>
                    <div className="font-display text-lg mt-2">Add a photo of your hair</div>
                    <div className="text-xs text-[hsl(var(--onyx)/0.55)]">
                      Drag &amp; drop or click to select · JPG / PNG / HEIC · up to {MAX_MB} MB
                    </div>
                  </button>
                )}
                <input
                  ref={inputRef}
                  type="file"
                  accept="image/*,.heic,.heif"
                  onChange={onSelect}
                  className="hidden"
                  data-testid="colour-match-photo-input"
                />
              </div>

              {/* Fields */}
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="overline">Full name *</label>
                  <input
                    type="text"
                    required
                    data-testid="colour-match-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-2 w-full h-12 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="overline">Email *</label>
                  <input
                    type="email"
                    required
                    data-testid="colour-match-email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-2 w-full h-12 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <label className="overline">Phone (optional)</label>
                  <input
                    type="tel"
                    data-testid="colour-match-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-2 w-full h-12 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
                    placeholder="+44 ..."
                  />
                </div>
                <div>
                  <label className="overline">Current hair</label>
                  <input
                    type="text"
                    data-testid="colour-match-current"
                    value={form.current_hair}
                    onChange={(e) => setForm({ ...form, current_hair: e.target.value })}
                    className="mt-2 w-full h-12 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
                    placeholder="e.g. Natural brunette, balayage on ends"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="overline">Desired look</label>
                  <input
                    type="text"
                    data-testid="colour-match-desired"
                    value={form.desired_look}
                    onChange={(e) => setForm({ ...form, desired_look: e.target.value })}
                    className="mt-2 w-full h-12 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
                    placeholder="e.g. Ashy blonde balayage · thickness + length"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="overline">Anything else? (optional)</label>
                  <textarea
                    rows={4}
                    maxLength={1200}
                    data-testid="colour-match-notes"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="mt-2 w-full rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 py-3 text-sm outline-none focus:border-[hsl(var(--onyx))] resize-none"
                    placeholder="Length, method preference, event date, tone preference..."
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="colour-match-submit"
                className="btn-pill btn-primary w-full mt-8 disabled:opacity-60"
              >
                {loading ? "Sending your photo…" : "Send my colour match request"}{" "}
                <ArrowUpRight size={16} />
              </button>

              <p className="mt-4 text-xs text-[hsl(var(--onyx)/0.5)] leading-relaxed">
                By submitting, you agree that we may email you a personalised
                shade recommendation. We won't share your photo with anyone else.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
